import React from 'react';
import PropTypes from 'prop-types';
import loadable from '@loadable/component';

import { bemGenerator } from '@prisjakt/ui-base';
import { AppContext } from '@prisjakt/ui-app-context';
import Breadcrumb from '@prisjakt/ui-breadcrumb';
import Pagination from '@prisjakt/ui-pagination';

import chunkArray from '../../../globals/helpers/chunk-array';
import PageContent from '../../../components/page-content';

import './index.scss';

const SotyVotePopup = loadable(() =>
  import(/* webpackChunkName: "shop-of-the-year" */ '../vote-popup')
);

const styles = bemGenerator('pj-soty-category', [
  'wrapper',
  'title',
  'section',
  'section-title',
  'section-shops',
  'shops-wrapper',
  'shop-link',
]);

class ShopOfTheYearCategoryNominees extends React.PureComponent {
  constructor(props) {
    super(props);

    this.state = {
      show_vote_popup: false,
    };

    this.openVotePopup = this.openVotePopup.bind(this);
    this.closeVotePopup = this.closeVotePopup.bind(this);
  }

  openVotePopup(category, nominee) {
    this.setState({
      show_vote_popup: {
        nominationCategory: category,
        shop: nominee,
      },
    });
  }

  closeVotePopup() {
    this.setState({
      show_vote_popup: false,
    });
  }

  static scrollToTop() {
    if (typeof window !== 'undefined') {
      window.scrollTo(0, 0);
    }
  }

  render() {
    const { t, linkTo } = this.context;
    const { name, id, nominees, logo } = this.props.data.shopOfTheYearCategory;
    const { charity, year, isVotingOpen } = this.props.data.shopOfTheYear;
    const { currentOffset } = this.props;

    const shopsByFirstLetter = nominees.nodes.reduce((sortedShops, shop) => {
      const firstLetter = shop.name.charAt(0).match(/\d/)
        ? '0-9'
        : shop.name.charAt(0).toUpperCase();
      const shopsWithSameFirstLetter = sortedShops[firstLetter] || [];
      return {
        ...sortedShops,
        [firstLetter]: [...shopsWithSameFirstLetter, shop],
      };
    }, {});

    const shops = Object.keys(shopsByFirstLetter).map(firstLetter => ({
      name: firstLetter,
      nodes: shopsByFirstLetter[firstLetter],
    }));

    return (
      <PageContent withAds>
        <Breadcrumb
          homeText={t('common', 'breadcrumb.home')}
          path={[
            {
              pathName: linkTo('shopOfTheYear'),
              name: t('campaigns', 'shop-of-the-year.title-with-year', {
                year,
              }),
              id: '0',
            },
            {
              pathName: linkTo('shopOfTheYearCategory', {
                year,
                id,
              }),
              name: t('campaigns', 'shop-of-the-year.category-title', {
                category: name,
              }),
              id: '1',
            },
          ]}
        />

        <div className={styles.block}>
          <h1 className={styles.title}>
            {t('campaigns', 'shop-of-the-year.category-title', {
              category: name,
            })}
          </h1>

          <div className={styles.wrapper}>
            {shops.map(section => (
              <div className={styles.section} key={`${section.name}}`}>
                <div className={styles['section-title']}>{section.name}</div>
                <div className={styles['shops-wrapper']}>
                  {chunkArray(section.nodes, 10).map((shop, index) => (
                    <div
                      key={`${section.name}-${index.toString()}`}
                      className={styles['section-shops']}
                    >
                      {shop.map(
                        chunkedShop =>
                          chunkedShop && (
                            <button
                              type="button"
                              key={chunkedShop.id}
                              className={styles['shop-link']}
                              onClick={() =>
                                this.openVotePopup(
                                  { id, name, logo },
                                  chunkedShop
                                )
                              }
                            >
                              {chunkedShop.name}
                            </button>
                          )
                      )}
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
          <Pagination
            pageInfo={{
              offset: currentOffset,
              limit: 100,
              hasNext: nominees.nodes.length >= 100,
              hasPrevious: currentOffset !== 0,
              onNext: ShopOfTheYearCategoryNominees.scrollToTop(),
              onPrevious: ShopOfTheYearCategoryNominees.scrollToTop(),
            }}
          />

          {isVotingOpen && this.state.show_vote_popup && (
            <SotyVotePopup
              onRequestClose={this.closeVotePopup}
              year={year}
              nominationCategory={this.state.show_vote_popup.nominationCategory}
              shop={this.state.show_vote_popup.shop}
              charity={charity}
            />
          )}
        </div>
      </PageContent>
    );
  }
}

const { shape, string, number, array } = PropTypes;
ShopOfTheYearCategoryNominees.propTypes = {
  data: shape({
    shopOfTheYearCategory: shape({
      name: string,
      id: number,
      nominees: shape({
        nodes: array,
      }),
    }),
    shopOfTheYear: shape({
      charity: shape({
        name: string,
        description: string,
        logo: string,
      }),
    }),
  }).isRequired,
  currentOffset: number,
};

ShopOfTheYearCategoryNominees.defaultProps = {
  currentOffset: 0,
};

ShopOfTheYearCategoryNominees.contextType = AppContext;

export default ShopOfTheYearCategoryNominees;
